// Invoice approvals:
//  • Credit approvals (client, 2026-11-04) — Admin / Super Admin only. A bill
//    for a customer over their credit limit or with an overdue bill goes ahead
//    on credit, but Accounts cannot Finalize & Post it until approved here.
//  • Field Executive invoice requests (client, 2026-10-07).
// Super Admin / Admin / Department Administrator: check the request, set or
// correct the rates, approve (creates the draft invoice for Accounts to
// finalise) or reject with a reason. The field executive is notified.
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { AlertTriangle, CheckCircle2, ClipboardCheck, Paperclip, XCircle } from 'lucide-react';
import { Spinner } from '@/shared/components/ui/Spinner';
import { errMsg, inr, STATUS, useApprovalActions, useCreditApprovals, useCreditDecision, useFieldApprovals, type CreditApproval, type FieldRequestRow } from '../api';
import { useAuthStore } from '@/shared/stores/auth.store';

const TABS = [
  { key: 'requested', label: 'Waiting' }, { key: 'approved', label: 'Approved' }, { key: 'sent', label: 'Issued' }, { key: 'rejected', label: 'Rejected' }, { key: 'all', label: 'All' },
];

export function FieldApprovalsPage() {
  const role = useAuthStore((st) => st.user?.role);
  const creditApprover = role === 'admin' || role === 'superadmin';
  const [section, setSection] = useState<'credit' | 'field'>(creditApprover ? 'credit' : 'field');
  const credit = useCreditApprovals('pending', creditApprover);
  return (
    <div className="mx-auto max-w-4xl space-y-4 pb-16">
      <div>
        <h1 className="flex items-center gap-2 text-xl font-bold text-on-surface"><ClipboardCheck className="h-5 w-5 text-success-text" /> Invoice Approvals</h1>
        <p className="text-xs text-on-surface-variant">
          {section === 'credit'
            ? 'Bills for customers over their credit limit or with an overdue bill. The draft invoice is already made; Accounts can post it once you approve the credit.'
            : 'Invoice requests raised by field executives. Approve to create the draft invoice (Accounts finalises it), or reject with a reason.'}
        </p>
      </div>
      {creditApprover && (
        <div className="flex gap-2">
          {([['credit', 'Credit approvals'], ['field', 'Field executive requests']] as const).map(([key, label]) => (
            <button key={key} type="button" onClick={() => setSection(key)}
              className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${section === key ? 'border-success bg-success text-white' : 'border-border text-on-surface-variant'}`}>
              {label}{key === 'credit' && credit.data?.pending ? <span className="ml-1 rounded-full bg-warning px-1.5 text-data-xs text-white">{credit.data.pending}</span> : null}
            </button>
          ))}
        </div>
      )}
      {section === 'credit' ? <CreditApprovalsSection /> : <FieldRequestsSection />}
    </div>
  );
}

const CREDIT_TABS = [
  { key: 'pending', label: 'Waiting' }, { key: 'approved', label: 'Approved' }, { key: 'rejected', label: 'Rejected' }, { key: 'all', label: 'All' },
];

function CreditApprovalsSection() {
  const [tab, setTab] = useState('pending');
  const { data, isLoading } = useCreditApprovals(tab);
  return (
    <>
      <div className="flex gap-1 overflow-x-auto rounded-xl bg-muted p-1">
        {CREDIT_TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)} className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold ${tab === t.key ? 'bg-card text-success-text shadow-xs' : 'text-on-surface-variant'}`}>
            {t.label}{t.key === 'pending' && data?.pending ? <span className="ml-1 rounded-full bg-warning px-1.5 text-data-xs text-white">{data.pending}</span> : null}
          </button>
        ))}
      </div>
      {isLoading ? <div className="flex justify-center py-16"><Spinner /></div> : !data?.items.length ? (
        <p className="rounded-2xl border border-dashed border-border bg-card p-10 text-center text-sm text-on-surface-variant">Nothing here.</p>
      ) : (
        <ul className="space-y-3">{data.items.map((r) => <CreditCard key={r.id} r={r} />)}</ul>
      )}
    </>
  );
}

function CreditCard({ r }: { r: FieldRequestRow }) {
  const decide = useCreditDecision();
  const ca = r.creditApproval!;
  const [note, setNote] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const act = async (decision: 'approve' | 'reject') => {
    try {
      await decide.mutateAsync({ id: r.id, decision, note: note.trim() || undefined });
      toast.success(decision === 'approve' ? `Credit approved — Accounts can post ${r.number}.` : `Credit rejected for ${r.number}.`);
      setRejecting(false);
    } catch (e) { toast.error(errMsg(e, 'Could not record the decision.')); }
  };
  return (
    <li className="rounded-card border border-border bg-card p-4 shadow-xs">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-bold text-on-surface">{r.clientName}</p>
          <p className="text-xs text-on-surface-variant">{r.number} · by <b>{r.requestedByName}</b> · {new Date(r.requestedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
            {' · '}{r.invoiceNumber ? `posted as ${r.invoiceNumber}` : r.invoiceId ? 'draft invoice ready' : 'draft not made yet'}</p>
        </div>
        <span className="text-sm font-bold tabular-nums text-on-surface">{inr(r.total)}</span>
      </div>
      <CreditPanel ca={ca} />
      {ca.status === 'pending' && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
          <input className="min-w-[200px] flex-1 rounded-lg border border-border px-2 py-1.5 text-xs outline-none focus:border-success/30"
            placeholder={rejecting ? 'Reason for rejecting (Sales sees it) — required' : 'Note (optional), e.g. payment promised by 15th'}
            value={note} onChange={(e) => setNote(e.target.value)} />
          {rejecting ? (
            <>
              <button type="button" onClick={() => setRejecting(false)} className="rounded-lg px-3 py-1.5 text-xs font-semibold text-on-surface-variant">Cancel</button>
              <button type="button" disabled={!note.trim() || decide.isPending} onClick={() => act('reject')} className="rounded-lg bg-danger px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">Reject credit</button>
            </>
          ) : (
            <>
              <button type="button" onClick={() => setRejecting(true)} className="inline-flex items-center gap-1 rounded-lg border border-danger/30 bg-danger/10 px-3 py-1.5 text-xs font-semibold text-danger-text"><XCircle className="h-4 w-4" /> Reject</button>
              <button type="button" disabled={decide.isPending} onClick={() => act('approve')} className="inline-flex items-center gap-1 rounded-lg bg-success px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"><CheckCircle2 className="h-4 w-4" /> {decide.isPending ? 'Saving…' : 'Approve credit'}</button>
            </>
          )}
        </div>
      )}
    </li>
  );
}

/** What the credit check found, and who decided. */
function CreditPanel({ ca }: { ca: CreditApproval }) {
  const cell = (label: string, value: string, tone = 'text-on-surface') => (
    <div><p className="text-data-xs uppercase tracking-wide text-on-surface-variant">{label}</p><p className={`text-sm font-semibold tabular-nums ${tone}`}>{value}</p></div>
  );
  return (
    <div className={`mt-3 rounded-xl border p-3 ${ca.status === 'approved' ? 'border-success/30 bg-success/5' : 'border-warning/30 bg-warning/5'}`}>
      <p className="flex items-start gap-1.5 text-xs font-semibold text-warning-text"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{ca.message}</p>
      <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {cell('Credit limit', ca.limit ? inr(ca.limit) : 'Not set')}
        {cell('Outstanding', inr(ca.outstanding))}
        {cell('Overdue', ca.overdueAmount ? `${inr(ca.overdueAmount)} · ${ca.overdueCount}` : '—', ca.overdueAmount ? 'text-danger-text' : 'text-on-surface')}
        {cell('After this bill', inr(ca.projected), ca.reasons.includes('over_limit') ? 'text-danger-text' : 'text-on-surface')}
      </div>
      {!!ca.oldDues && <p className="mt-1.5 text-data-xs text-on-surface-variant">Outstanding includes {inr(ca.oldDues)} old dues from before the CRM.</p>}
      {ca.status !== 'pending' && (
        <p className={`mt-1.5 text-xs ${ca.status === 'approved' ? 'text-success-text' : 'text-danger-text'}`}>
          Credit {ca.status} by {ca.decidedByName || '—'}{ca.decidedAt ? ` · ${new Date(ca.decidedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}{ca.note ? ` — ${ca.note}` : ''}
        </p>
      )}
    </div>
  );
}

function FieldRequestsSection() {
  const [tab, setTab] = useState('requested');
  const { data, isLoading } = useFieldApprovals(tab);
  return (
    <>
      <div className="flex gap-1 overflow-x-auto rounded-xl bg-muted p-1">
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)} className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold ${tab === t.key ? 'bg-card text-success-text shadow-xs' : 'text-on-surface-variant'}`}>
            {t.label}{t.key === 'requested' && data?.pending ? <span className="ml-1 rounded-full bg-warning px-1.5 text-data-xs text-white">{data.pending}</span> : null}
          </button>
        ))}
      </div>
      {isLoading ? <div className="flex justify-center py-16"><Spinner /></div> : !data?.items.length ? (
        <p className="rounded-2xl border border-dashed border-border bg-card p-10 text-center text-sm text-on-surface-variant">Nothing here.</p>
      ) : (
        <ul className="space-y-3">{data.items.map((r) => <ApprovalCard key={r.id} r={r} />)}</ul>
      )}
    </>
  );
}

function ApprovalCard({ r }: { r: FieldRequestRow }) {
  const { approve, reject } = useApprovalActions();
  const waiting = r.status === 'requested';
  const [rates, setRates] = useState<string[]>((r.items || []).map((it) => (it.rate ? String(it.rate) : '')));
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const items = r.items || [];
  const total = items.reduce((n, it, i) => n + (Number(rates[i]) || 0) * it.quantity * (1 + (Number(it.taxRate ?? 18) / 100)), 0);
  const ratesOk = items.every((_, i) => Number(rates[i]) > 0);
  const field = 'w-28 rounded-lg border border-border px-2 py-1.5 text-right text-sm outline-none focus:border-success/30';

  return (
    <li className="rounded-card border border-border bg-card p-4 shadow-xs">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-bold text-on-surface">{r.clientName}</p>
          <p className="text-xs text-on-surface-variant">{r.number} · by <b>{r.requestedByName}</b> · {new Date(r.requestedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-data-xs font-semibold ring-1 ${STATUS[r.status].cls}`}>{STATUS[r.status].label}</span>
      </div>
      <table className="mt-3 w-full text-sm">
        <thead><tr className="text-left text-data-xs uppercase tracking-wide text-on-surface-variant"><th className="py-1">Product</th><th className="py-1 text-right">Qty</th><th className="py-1 text-right">Rate ₹ (ex-GST)</th><th className="py-1 text-right">GST</th></tr></thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={i} className="border-t border-border">
              <td className="py-1.5 pr-2 text-on-surface">{it.name}{it.packSize ? <span className="text-xs text-on-surface-variant"> · {it.packSize}</span> : null}</td>
              <td className="py-1.5 text-right tabular-nums">{it.quantity}</td>
              <td className="py-1.5 text-right">{waiting ? <input className={field} inputMode="decimal" value={rates[i] ?? ''} onChange={(e) => setRates((x) => x.map((v, j) => (j === i ? e.target.value : v)))} placeholder="Rate" /> : inr(it.rate)}</td>
              <td className="py-1.5 text-right text-xs text-on-surface-variant">{it.taxRate ?? 18}%</td>
            </tr>
          ))}
        </tbody>
      </table>
      {r.notes && <p className="mt-2 rounded-lg bg-muted px-3 py-2 text-xs text-on-surface-variant">“{r.notes}”</p>}
      {(r.poAttachments || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">{r.poAttachments!.map((f, i) => <a key={i} href={f.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-lg border border-border px-2 py-1 text-xs text-on-surface-variant"><Paperclip className="h-3 w-3" />{f.name || 'Customer PO'}</a>)}</div>
      )}
      {r.status === 'rejected' && r.rejectReason && <p className="mt-2 text-xs text-danger-text">Rejected by {r.reviewedByName}: {r.rejectReason}</p>}
      {['approved', 'sent'].includes(r.status) && r.reviewedByName && <p className="mt-2 text-xs text-success-text">Approved by {r.reviewedByName}{r.invoiceNumber ? ` · invoice ${r.invoiceNumber}` : ' · draft invoice waiting for Accounts to finalise'}</p>}

      {waiting && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
          <span className="text-sm text-on-surface-variant">Total with GST: <b className="text-on-surface">{inr(total)}</b></span>
          <div className="flex gap-2">
            <button type="button" onClick={() => setRejecting(true)} className="inline-flex items-center gap-1 rounded-lg border border-danger/30 bg-danger/10 px-3 py-1.5 text-xs font-semibold text-danger-text"><XCircle className="h-4 w-4" /> Reject</button>
            <button type="button" disabled={!ratesOk || approve.isPending} title={ratesOk ? '' : 'Enter a rate for every product'} onClick={async () => {
              try {
                await approve.mutateAsync({ id: r.id, items: items.map((it, i) => ({ rate: Number(rates[i]), quantity: it.quantity })) });
                toast.success(`${r.number} approved — draft invoice created for Accounts.`);
              } catch (e) { toast.error(errMsg(e, 'Could not approve.')); }
            }} className="inline-flex items-center gap-1 rounded-lg bg-success px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"><CheckCircle2 className="h-4 w-4" /> {approve.isPending ? 'Approving…' : 'Approve'}</button>
          </div>
        </div>
      )}
      {rejecting && (
        <div className="mt-3 rounded-xl border border-danger/30 bg-danger/10/50 p-3">
          <label className="block text-xs font-semibold text-danger-text">Reason (the field executive sees it)
            <textarea className="mt-1 w-full rounded-lg border border-danger/30 bg-card px-3 py-2 text-sm outline-none" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
          </label>
          <div className="mt-2 flex justify-end gap-2">
            <button type="button" onClick={() => setRejecting(false)} className="rounded-lg px-3 py-1.5 text-xs font-semibold text-on-surface-variant">Cancel</button>
            <button type="button" disabled={!reason.trim() || reject.isPending} onClick={async () => {
              try { await reject.mutateAsync({ id: r.id, reason: reason.trim() }); toast.success(`${r.number} rejected.`); setRejecting(false); } catch (e) { toast.error(errMsg(e, 'Could not reject.')); }
            }} className="rounded-lg bg-danger px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">Reject request</button>
          </div>
        </div>
      )}
    </li>
  );
}

export default FieldApprovalsPage;
